// apps/web/src/lib/chartColors.ts
// Chart/SVG colors as resolved strings. SVG presentation attributes (stroke/fill)
// and Recharts props don't resolve CSS var(), so charts can't use the Tailwind
// token classes — they need concrete colors. These values MIRROR the CSS tokens
// in index.css (dark + light blocks); keep them in sync. The hooks subscribe to
// the theme store, so charts repaint when the user flips Appearance.
import { useTheme, type Theme } from "./theme";

type ChartColorName =
  | "accent-cal" | "accent-protein" | "accent-move"
  | "success" | "warn" | "danger"
  | "chart-grid" | "chart-axis" | "elevated"
  | "ring-over";

const PALETTE: Record<Theme, Record<ChartColorName, string>> = {
  dark: {
    "accent-cal": "#fa114f",
    "accent-protein": "#67d491",
    "accent-move": "#06b6d4",
    success: "#10b981",
    warn: "#fbbf24",
    danger: "#fb7185",
    "chart-grid": "#334155",
    "chart-axis": "#94a3b8",
    elevated: "#18181b",
    // neutral "over target" ring — deliberately not a red (U3 tone rules)
    "ring-over": "rgba(255,255,255,0.45)",
  },
  light: {
    "accent-cal": "#db0f45",
    "accent-protein": "#15803d",
    "accent-move": "#0891b2",
    success: "#047857",
    warn: "#a14a08",
    danger: "#be123c",
    "chart-grid": "#dbe3dc",
    "chart-axis": "#55645a",
    elevated: "#ffffff",
    "ring-over": "rgba(16,23,17,0.40)",
  },
};

/** Resolved chart color for the active theme. */
export function useChartColor(name: ChartColorName): string {
  return PALETTE[useTheme()][name];
}

/** Several at once, keyed by name. */
export function useChartColors<T extends ChartColorName>(names: readonly T[]): Record<T, string> {
  const palette = PALETTE[useTheme()];
  const out = {} as Record<T, string>;
  for (const n of names) out[n] = palette[n];
  return out;
}
